'use client'

import { useState, useRef } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import type { Stylist } from "@/components/site/BookingModal";
import { stylists } from "./stylists";
import Image from "next/image";
import { formatPrice } from "@/lib/formatters";

type Props = { onSelect: (s: Stylist) => void };

function StylistCard({ s, i, onSelect }: { s: Stylist; i: number; onSelect: (s: Stylist) => void }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: i * 0.08 }}
      className="group"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-secondary">
        <Image
          src={s.image}
          alt={s.name}
          width={768}
          height={1024}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute right-3 top-3 bg-nude/90 px-2 py-1 font-mono text-[0.6rem]">
          N° 0{i + 1}
        </div>
      </div>
      <div className="mt-5">
        <p className="eyebrow">{s.specialty}</p>
        <h3 className="mt-2 font-display text-2xl">{s.name}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {s.service} · {formatPrice(s.price)}
        </p>
        <button onClick={() => onSelect(s)} className="mt-5 w-full btn-outline-editorial">
          View Availability
        </button>
      </div>
    </motion.article>
  );
}

function MobileCarousel({ onSelect }: { onSelect: (s: Stylist) => void }) {
  const [current, setCurrent] = useState(0);
  const x = useMotionValue(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const CARD_WIDTH = 280;
  const GAP = 16;
  const STEP = CARD_WIDTH + GAP;

  const goTo = (index: number) => {
    const clamped = Math.max(0, Math.min(index, stylists.length - 1));
    setCurrent(clamped);
    animate(x, -clamped * STEP, { type: "spring", stiffness: 300, damping: 30 });
  };

  const handleDragEnd = (_: never, info: { offset: { x: number } }) => {
    if (info.offset.x < -50 && current < stylists.length - 1) {
      goTo(current + 1);
    } else if (info.offset.x > 50 && current > 0) {
      goTo(current - 1);
    } else {
      animate(x, -current * STEP, { type: "spring", stiffness: 300, damping: 30 });
    }
  };

  return (
    <div className="lg:hidden">
      <div ref={containerRef} className="overflow-hidden px-6">
        <motion.div
          style={{ x }}
          drag="x"
          dragConstraints={{ left: -(stylists.length - 1) * STEP, right: 0 }}
          dragElastic={0.1}
          onDragEnd={handleDragEnd}
          className="flex gap-4 cursor-grab active:cursor-grabbing"
        >
          {stylists.map((s, i) => (
            <div key={s.name} className="w-[280px] flex-shrink-0 select-none">
              <StylistCard s={s} i={i} onSelect={onSelect} />
            </div>
          ))}
        </motion.div>
      </div>

      <div className="mt-8 flex items-center justify-center gap-2">
        {stylists.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-px transition-all duration-300 ${
              i === current
                ? "w-8 bg-ink"
                : "w-4 bg-border hover:bg-muted-foreground"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export function StylistsSection({ onSelect }: Props) {
  return (
    <section id="stylists" className="py-20">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">N° 02 — The Professionals</p>
            <h2 className="mt-4 max-w-2xl font-display text-5xl md:text-7xl">
              Hands trained in the world's<br />finest <em>maisons</em>.
            </h2>
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            Every artist is hand-selected and apprenticed for a decade before joining the floor. Choose yours.
          </p>
        </div>
      </div>

      <MobileCarousel onSelect={onSelect} />

      <div className="mx-auto hidden max-w-[1600px] px-6 md:px-12 lg:block">
        <div className="grid grid-cols-4 gap-8">
          {stylists.map((s, i) => (
            <StylistCard key={s.name} s={s} i={i} onSelect={onSelect} />
          ))}
        </div>
      </div>
    </section>
  );
}
